import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  useColorModeValue,
  Button,
  HStack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Text,
  Container,
  Flex,
  IconButton,
  VStack,
  Badge,
  useBreakpointValue,
  Progress,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  useDisclosure,
  Slide
} from '@chakra-ui/react';
import { 
  ArrowLeft, 
  ArrowRight, 
  FloppyDisk, 
  Check, 
  List, 
  LightbulbFilament,
  CaretUp,
  CaretDown
} from '@phosphor-icons/react';
import { AnimatePresence, motion } from 'framer-motion';
import { useCreateSite } from '../../context/CreateSiteContext';
import { SiteCreationSidebar } from './SiteCreationSidebar';
import { BasicInfoStep } from './steps/BasicInfoStep';
import { LocationStep } from './steps/LocationStep';
import { TimelineStep } from './steps/TimelineStep';
import { MediaStep } from './steps/MediaStep';
import { ReviewStep } from './steps/ReviewStep';
import { AIAssistant } from './AIAssistant';
import { DraftManager } from './DraftManager';

const MotionBox = motion(Box);
const MotionFlex = motion(Flex);

export const CreateSiteWizard = ({ draftIdToLoad }) => {
  const {
    currentStep,
    steps,
    canProceed,
    isDirty,
    handleNext,
    handlePrev,
    handleSaveAsDraft,
    handleSubmit,
    showExitModal,
    setShowExitModal,
    savingDraft,
    loadDrafts,
    loadDraftById
  } = useCreateSite();
  
  const [assistantVisible, setAssistantVisible] = useState(true);
  const [isBottomNavVisible, setIsBottomNavVisible] = useState(true);
  const [lastScrollPosition, setLastScrollPosition] = useState(0);
  
  const { isOpen: isSidebarOpen, onOpen: onSidebarOpen, onClose: onSidebarClose } = useDisclosure();
  
  const isMobile = useBreakpointValue({ base: true, md: false });
  const bgColor = useColorModeValue('white', 'bg.surface');
  const borderColor = useColorModeValue('gray.200', 'border.default');
  const secondaryBgColor = useColorModeValue('gray.50', 'bg.default');
  
  // Load draft data if draftId is provided as prop
  useEffect(() => {
    if (draftIdToLoad) {
      loadDrafts().then(() => {
        loadDraftById(draftIdToLoad);
      });
    }
  }, [draftIdToLoad, loadDrafts, loadDraftById]);

  // Handle scroll direction for bottom navigation on mobile
  useEffect(() => {
    if (!isMobile) return;
    
    const handleScroll = () => {
      const currentScrollPos = window.pageYOffset;
      const isScrollingDown = currentScrollPos > lastScrollPosition;
      
      // Only hide when scrolling down and not near the top
      if (isScrollingDown && currentScrollPos > 100) {
        setIsBottomNavVisible(false);
      } else {
        setIsBottomNavVisible(true);
      }
      
      setLastScrollPosition(currentScrollPos);
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollPosition, isMobile]);
  
  // Calculate progress percentage
  const progress = ((currentStep + 1) / steps.length) * 100;
  
  // Determine which step component to display
  const renderStep = () => {
    switch(currentStep) {
      case 0:
        return <BasicInfoStep />;
      case 1:
        return <LocationStep />;
      case 2:
        return <TimelineStep />;
      case 3:
        return <MediaStep />;
      case 4:
        return <ReviewStep />;
      default:
        return <BasicInfoStep />;
    }
  };
  
  return (
    <Container maxW="container.xl" py={{ base: 4, md: 8 }} px={{ base: 2, md: 4 }}>
      {/* Page title for mobile */}
      {isMobile && (
        <Box mb={4}>
          <Flex justify="space-between" align="center" mb={2}>
            <HStack>
              <IconButton
                icon={<List />}
                variant="ghost"
                onClick={onSidebarOpen}
                aria-label="Open navigation"
              />
              <Text fontWeight="bold" fontSize="lg">
                Create Megalithic Site
              </Text>
            </HStack>
            
            <IconButton
              icon={<LightbulbFilament />}
              variant="ghost"
              colorScheme="yellow"
              onClick={() => setAssistantVisible(!assistantVisible)}
              aria-label="Toggle AI assistant"
            />
          </Flex>
          
          {/* Step indicator for mobile */}
          <HStack mb={2} spacing={1} fontSize="sm">
            <Badge colorScheme="yellow" px={2}>
              Step {currentStep + 1} of {steps.length}
            </Badge>
            <Text fontWeight="medium">{steps[currentStep].title}</Text>
          </HStack>
          
          {/* Progress bar */}
          <Progress 
            value={progress} 
            size="xs" 
            colorScheme="yellow"
            borderRadius="full"
            bg={useColorModeValue('gray.100', 'gray.700')}
          />
        </Box>
      )}
      
      <Grid 
        templateColumns={{ base: '1fr', md: '280px 1fr' }} 
        gap={{ base: 6, md: 0 }}
        minHeight={{ md: 'calc(100vh - 180px)' }} // Adjust based on header/footer height
        bg={secondaryBgColor}
        borderRadius={{ base: "lg", md: "xl" }}
        overflow="hidden"
        boxShadow="lg"
      >
        {/* Left panel - Navigation (desktop only) */}
        {!isMobile && <SiteCreationSidebar currentStep={currentStep} />}
        
        {/* Mobile Sidebar Drawer */}
        <Drawer
          isOpen={isSidebarOpen}
          placement="left"
          onClose={onSidebarClose}
          size="sm"
        >
          <DrawerOverlay />
          <DrawerContent>
            <DrawerCloseButton />
            <DrawerHeader borderBottomWidth="1px">Create Megalithic Site</DrawerHeader>
            <DrawerBody p={0}>
              <SiteCreationSidebar currentStep={currentStep} onNavigate={onSidebarClose} />
            </DrawerBody>
          </DrawerContent>
        </Drawer>
        
        {/* Right panel - Active content */}
        <Box
          bg={bgColor}
          borderLeftWidth={{ md: "1px" }}
          borderLeftColor={borderColor}
          p={{ base: 4, md: 6 }}
          borderRadius={{ base: "lg", md: 0 }}
          position="relative"
          display="flex"
          flexDirection="column"
          minW="0" // Added for grid item flex behavior
          minH="0" // Added for flex child behavior
        >
          {/* Scrollable content area */}
          <Box
            flexGrow={1}
            overflowY="auto"
            overflowX="hidden"
            minH="0" // Added to help flexbox correctly calculate overflow
            pb={{ base: 140, md: 80 }} /* Extra padding at bottom to ensure content doesn't hide behind buttons */
            css={{
              "&::-webkit-scrollbar": {
                width: "4px",
              },
              "&::-webkit-scrollbar-track": {
                width: "6px",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "gray.300",
                borderRadius: "24px",
              },
            }}
          >
            <AnimatePresence mode="wait">
              <MotionBox
                key={currentStep}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
                minH="0" // Helps flex child sizing
                flexGrow={1} // Allow MotionBox to grow within its flex parent
                display="flex" // Make it a flex container for TimelineStep
                flexDirection="column" // Stack TimelineStep vertically
              >
                {renderStep()}
              </MotionBox>
            </AnimatePresence>
          </Box>
          
          {/* Navigation buttons - fixed at the bottom */}
          <Slide direction="bottom" in={isBottomNavVisible} style={{ zIndex: 10 }}>
            <Box 
              position={{ base: 'fixed', md: 'absolute' }}
              bottom={0}
              left={0}
              right={0}
              pt={4} 
              pb={{ base: 6, md: 4 }}
              px={{ base: 4, md: 8 }}
              borderTopWidth="1px" 
              borderColor={borderColor}
              bg={bgColor}
              width="100%"
              boxShadow="0 -4px 10px rgba(0, 0, 0, 0.1)"
              borderTopRadius={{ base: "lg", md: 0 }}
            >
              {/* Mobile-friendly navigation buttons */}
              {isMobile ? (
                <VStack spacing={3}>
                  <HStack w="100%" justify="space-between">
                    <Button
                      onClick={handlePrev}
                      isDisabled={currentStep === 0}
                      variant="outline"
                      leftIcon={<ArrowLeft />}
                      size="md"
                      flex={1}
                    >
                      Back
                    </Button>
                    
                    {/* Only show Next button when not on the Review step */}
                    {currentStep < steps.length - 1 && (
                      <Button
                        onClick={handleNext}
                        isDisabled={!canProceed}
                        colorScheme="yellow"
                        rightIcon={<ArrowRight />}
                        size="md"
                        flex={2}
                        ml={2}
                      >
                        Next
                      </Button>
                    )}
                  </HStack>
                  
                  <HStack w="100%" spacing={2}>
                    <DraftManager isMobile={true} />
                    <Button
                      onClick={handleSaveAsDraft}
                      variant="ghost"
                      colorScheme="yellow"
                      leftIcon={<FloppyDisk />}
                      size="sm"
                      flex={1}
                      isLoading={savingDraft}
                      loadingText="Saving..."
                    >
                      Save Draft
                    </Button>
                  </HStack>
                </VStack>
              ) : (
                // Desktop navigation
                <HStack justify="space-between">
                  <Button
                    leftIcon={<ArrowLeft />}
                    onClick={handlePrev}
                    isDisabled={currentStep === 0}
                    variant="ghost"
                  >
                    Back
                  </Button>
                  
                  <HStack>
                    <DraftManager />
                    <Button
                      leftIcon={<FloppyDisk />}
                      onClick={handleSaveAsDraft}
                      variant="outline"
                      colorScheme="yellow"
                      isLoading={savingDraft}
                      loadingText="Saving..."
                    >
                      Save Draft
                    </Button>
                    
                    {/* Only show Next button when not on the Review step */}
                    {currentStep < steps.length - 1 && (
                      <Button
                        rightIcon={<ArrowRight />}
                        onClick={handleNext}
                        isDisabled={!canProceed}
                        colorScheme="yellow"
                      >
                        Next: {steps[currentStep + 1]?.title || 'Next'}
                      </Button>
                    )}
                  </HStack>
                </HStack>
              )}
            </Box>
          </Slide>
        </Box>
      </Grid>
      
      {/* AI Assistant */}
      <AIAssistant
        isVisible={assistantVisible}
        onToggleVisibility={() => setAssistantVisible(!assistantVisible)}
      />
      
      {/* Exit confirmation modal */}
      <Modal isOpen={showExitModal} onClose={() => setShowExitModal(false)}>
        <ModalOverlay />
        <ModalContent mx={4}>
          <ModalHeader>Unsaved Changes</ModalHeader>
          <ModalBody>
            <Text>
              You have unsaved changes. Would you like to save your progress as a draft before leaving?
            </Text>
          </ModalBody>
          <ModalFooter flexDirection={{ base: "column", md: "row" }} alignItems="stretch">
            <VStack spacing={3} width="100%">
              <Button 
                colorScheme="yellow"
                width="100%"
                onClick={async () => {
                  await handleSaveAsDraft();
                  setShowExitModal(false);
                }}
              >
                Save as Draft
              </Button>
              <Button 
                variant="outline"
                width="100%" 
                onClick={() => setShowExitModal(false)}
              >
                Continue Editing
              </Button>
              <Button 
                variant="ghost" 
                colorScheme="red"
                width="100%"
                onClick={() => {
                  setShowExitModal(false);
                }}
              >
                Discard Changes
              </Button>
            </VStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Container>
  );
};
